/**
 * Channels & Subscribers API hooks — notification destinations (Phase 1).
 * Channels are tenant-scoped (no project_id). `type=null` = multi-type.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { useToast } from '@vnetwork/ui/feedback';
import {
  healthcheckApi,
  hcKeys,
  hcExtractError,
  unwrap,
  unwrapList,
  type Channel,
  type ChannelType,
  type Subscriber,
  type SubscriberScope,
  type SubscriberStatus,
} from './_shared';

// ---- Input types ---------------------------------------------------------

export interface CreateChannelInput {
  name: string;
  type?: ChannelType | null;
  config: Record<string, unknown>;
}

export interface UpdateChannelInput {
  name?: string;
  config?: Record<string, unknown>;
  is_active?: boolean;
}

export interface CreateSubscriberInput {
  scope: SubscriberScope;
  key: string;
  name?: string;
  status?: SubscriberStatus;
}

export interface UpdateSubscriberInput {
  scope?: SubscriberScope;
  key?: string;
  name?: string;
  status?: SubscriberStatus;
  is_active?: boolean;
}

/** Response from POST /channels/:id/test */
export interface TestChannelResult {
  ok: boolean;
  total_subscribers: number;
  error_count: number;
  errors?: string[];
}

// ---- Channel queries -----------------------------------------------------

export function useChannels() {
  return useQuery<Channel[]>({
    queryKey: hcKeys.channels(),
    staleTime: 15_000,
    queryFn: async () => unwrapList<Channel>(await healthcheckApi.get('/channels')).items,
  });
}

export function useChannel(id: string | undefined) {
  return useQuery<Channel>({
    queryKey: hcKeys.channel(id ?? ''),
    enabled: !!id,
    staleTime: 15_000,
    queryFn: async () => unwrap<Channel>(await healthcheckApi.get(`/channels/${id}`)),
  });
}

// ---- Channel mutations ---------------------------------------------------

export function useCreateChannel(
  options?: UseMutationOptions<Channel, Error, CreateChannelInput>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Channel, Error, CreateChannelInput>({
    mutationFn: async (input) =>
      unwrap<Channel>(await healthcheckApi.post('/channels', input)),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: hcKeys.channels() });
      show({ tone: 'success', title: `Channel "${args[0].name}" created` });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Create failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useUpdateChannel(
  id: string,
  options?: UseMutationOptions<Channel, Error, UpdateChannelInput>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Channel, Error, UpdateChannelInput>({
    mutationFn: async (input) =>
      unwrap<Channel>(await healthcheckApi.put(`/channels/${id}`, input)),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: hcKeys.channel(id) });
      qc.invalidateQueries({ queryKey: hcKeys.channels() });
      show({ tone: 'success', title: 'Channel updated' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Update failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useDeleteChannel(options?: UseMutationOptions<void, Error, string>) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await healthcheckApi.delete(`/channels/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: hcKeys.channels() });
      show({ tone: 'success', title: 'Channel deleted' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Delete failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useTestChannel() {
  return useMutation<TestChannelResult, Error, string>({
    mutationFn: async (id) =>
      unwrap<TestChannelResult>(await healthcheckApi.post(`/channels/${id}/test`)),
  });
}

// ---- Subscriber queries --------------------------------------------------

export function useSubscribers(channelId: string | undefined) {
  return useQuery<Subscriber[]>({
    queryKey: hcKeys.subscribers(channelId ?? ''),
    enabled: !!channelId,
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<Subscriber>(
        await healthcheckApi.get(`/channels/${channelId}/subscribers`),
      ).items,
  });
}

// ---- Subscriber mutations ------------------------------------------------

export function useCreateSubscriber(
  channelId: string,
  options?: UseMutationOptions<Subscriber, Error, CreateSubscriberInput>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Subscriber, Error, CreateSubscriberInput>({
    mutationFn: async (input) =>
      unwrap<Subscriber>(
        await healthcheckApi.post(`/channels/${channelId}/subscribers`, input),
      ),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: hcKeys.subscribers(channelId) });
      qc.invalidateQueries({ queryKey: hcKeys.channel(channelId) });
      show({ tone: 'success', title: 'Subscriber added' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Add failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useUpdateSubscriber(
  channelId: string,
  subId: string,
  options?: UseMutationOptions<Subscriber, Error, UpdateSubscriberInput>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Subscriber, Error, UpdateSubscriberInput>({
    mutationFn: async (input) =>
      unwrap<Subscriber>(
        await healthcheckApi.put(`/channels/${channelId}/subscribers/${subId}`, input),
      ),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: hcKeys.subscribers(channelId) });
      show({ tone: 'success', title: 'Subscriber updated' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Update failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useDeleteSubscriber(
  channelId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<void, Error, string>({
    mutationFn: async (subId) => {
      await healthcheckApi.delete(`/channels/${channelId}/subscribers/${subId}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: hcKeys.subscribers(channelId) });
      qc.invalidateQueries({ queryKey: hcKeys.channel(channelId) });
      show({ tone: 'success', title: 'Subscriber removed' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Delete failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

// Re-export types for convenience
export type { Channel, ChannelType, Subscriber, SubscriberScope, SubscriberStatus };
